/**
 * Typography utilities — single source of truth for the 3-font system.
 * Shared by TypographyTogglePanel, /dev/typography diagnostics, and a11y checker.
 *
 * Persistence: localStorage (per-browser) + optional profile sync (cross-device).
 */
import { supabase } from "@/integrations/supabase/client";

export type FontRole = "display" | "body" | "mono";

/** CSS variables we manage. */
export const BANNER_TOKENS = [
  "--font-banner-label",
  "--font-banner-item",
  "--banner-label-size",
  "--banner-label-leading",
  "--banner-label-tracking",
  "--banner-label-weight",
  "--banner-label-case",
  "--banner-item-size",
  "--banner-item-leading",
  "--banner-item-tracking",
  "--banner-item-weight",
  "--banner-item-case",
] as const;

export const MOBILE_BANNER_TOKENS = [
  "--banner-label-size-mobile",
  "--banner-label-tracking-mobile",
  "--banner-item-size-mobile",
  "--banner-item-tracking-mobile",
] as const;

export type BannerToken = (typeof BANNER_TOKENS)[number];
export type MobileBannerToken = (typeof MOBILE_BANNER_TOKENS)[number];

export type Preset = {
  name: string;
  label: string;
  vars: Partial<Record<BannerToken, string>>;
  mobile?: Partial<Record<MobileBannerToken, string>>;
};

export const PRESETS: Preset[] = [
  {
    name: "default",
    label: "Default · Display + Inter",
    vars: {
      "--font-banner-label": "var(--font-display)",
      "--font-banner-item": "var(--font-sans)",
      "--banner-label-size": "11px",
      "--banner-label-tracking": "0.12em",
      "--banner-label-weight": "700",
      "--banner-label-case": "uppercase",
      "--banner-item-size": "13.5px",
      "--banner-item-tracking": "-0.005em",
      "--banner-item-weight": "500",
    },
    mobile: {
      "--banner-label-size-mobile": "11px",
      "--banner-label-tracking-mobile": "0.1em",
      "--banner-item-size-mobile": "14px",
      "--banner-item-tracking-mobile": "0",
    },
  },
  {
    name: "compact",
    label: "Compact Terminal",
    vars: {
      "--font-banner-label": "var(--font-mono)",
      "--font-banner-item": "var(--font-sans)",
      "--banner-label-size": "10px",
      "--banner-label-tracking": "0.16em",
      "--banner-label-weight": "600",
      "--banner-label-case": "uppercase",
      "--banner-item-size": "12.5px",
      "--banner-item-tracking": "0",
      "--banner-item-weight": "500",
    },
  },
  {
    name: "editorial",
    label: "Editorial · Display Both",
    vars: {
      "--font-banner-label": "var(--font-display)",
      "--font-banner-item": "var(--font-display)",
      "--banner-label-size": "11px",
      "--banner-label-tracking": "0.14em",
      "--banner-label-weight": "700",
      "--banner-label-case": "uppercase",
      "--banner-item-size": "14px",
      "--banner-item-tracking": "-0.01em",
      "--banner-item-weight": "500",
    },
  },
  {
    name: "minimal",
    label: "Minimal · Body Both",
    vars: {
      "--font-banner-label": "var(--font-sans)",
      "--font-banner-item": "var(--font-sans)",
      "--banner-label-size": "11px",
      "--banner-label-tracking": "0.08em",
      "--banner-label-weight": "600",
      "--banner-label-case": "uppercase",
      "--banner-item-size": "13.5px",
      "--banner-item-tracking": "0",
      "--banner-item-weight": "500",
    },
  },
];

export const STORAGE_KEY = "vzl_banner_typo_v2";

export type StoredState = {
  preset: string;
  customVars?: Partial<Record<BannerToken, string>>;
  mobileVars?: Partial<Record<MobileBannerToken, string>>;
};

export function applyVars(vars: Partial<Record<string, string>>) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  Object.entries(vars).forEach(([k, v]) => {
    if (v) root.style.setProperty(k, v);
  });
}

export function clearAllManagedVars() {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  [...BANNER_TOKENS, ...MOBILE_BANNER_TOKENS].forEach((k) => root.style.removeProperty(k));
}

export function loadStored(): StoredState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredState;
  } catch {
    return null;
  }
}

export function saveStored(state: StoredState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {/* noop */}
}

export function applyState(state: StoredState) {
  const preset = PRESETS.find((p) => p.name === state.preset) ?? PRESETS[0];
  // 1. base preset vars
  applyVars(preset.vars);
  // 2. preset mobile defaults
  if (preset.mobile) applyVars(preset.mobile);
  // 3. custom desktop overrides
  if (state.customVars) applyVars(state.customVars);
  // 4. custom mobile overrides
  if (state.mobileVars) applyVars(state.mobileVars);
}

/** Read currently-resolved CSS variable from :root. */
export function readVar(name: string): string {
  if (typeof getComputedStyle === "undefined") return "";
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/** Returns the actual font family resolved for a given selector. */
export function readResolvedFontFamily(selector: string): string {
  if (typeof document === "undefined") return "";
  const el = document.querySelector(selector) as HTMLElement | null;
  if (!el) return "";
  return getComputedStyle(el).fontFamily;
}

/**
 * Sync banner tokens to the live glass card header element.
 * Reads the first `[data-glass-card-label]` and copies its computed
 * font family / size / tracking / weight into the LABEL tokens.
 * Items keep their current values (they are not card headers).
 */
export function syncToGlassCardHeader(): {
  ok: boolean;
  applied?: Partial<Record<BannerToken, string>>;
  reason?: string;
} {
  if (typeof document === "undefined") return { ok: false, reason: "ssr" };
  const el = document.querySelector("[data-glass-card-label]") as HTMLElement | null;
  if (!el) return { ok: false, reason: "No glass card header found on this page." };
  const cs = getComputedStyle(el);

  // Map font family to one of the role variables for cleaner persistence
  const fam = cs.fontFamily.toLowerCase();
  const familyVar = fam.includes("space grotesk")
    ? "var(--font-display)"
    : fam.includes("jetbrains")
    ? "var(--font-mono)"
    : "var(--font-sans)";

  const applied: Partial<Record<BannerToken, string>> = {
    "--font-banner-label": familyVar,
    "--banner-label-size": cs.fontSize,
    "--banner-label-tracking": cs.letterSpacing === "normal" ? "0" : cs.letterSpacing,
    "--banner-label-weight": cs.fontWeight,
    "--banner-label-case": cs.textTransform === "none" ? "none" : cs.textTransform,
  };
  applyVars(applied);
  return { ok: true, applied };
}

/* ────────────────────────────────────────────────────
 * Font load detection
 * ──────────────────────────────────────────────────── */

const FONT_CHECK_LIST: Array<{ family: string; role: FontRole }> = [
  { family: "Space Grotesk", role: "display" },
  { family: "Inter", role: "body" },
  { family: "JetBrains Mono", role: "mono" },
];

export type FontLoadStatus = {
  family: string;
  role: FontRole;
  loaded: boolean;
};

export async function checkFontsLoaded(): Promise<FontLoadStatus[]> {
  if (typeof document === "undefined" || !("fonts" in document)) {
    return FONT_CHECK_LIST.map((f) => ({ ...f, loaded: false }));
  }
  // ensure fonts have had a chance to load
  try { await (document as any).fonts.ready; } catch {/* noop */}
  return FONT_CHECK_LIST.map((f) => {
    let loaded = false;
    try {
      // 14px is required by spec; check 400 + 700
      loaded =
        (document as any).fonts.check(`400 14px "${f.family}"`) &&
        (document as any).fonts.check(`700 14px "${f.family}"`);
    } catch {/* noop */}
    return { ...f, loaded };
  });
}

/* ────────────────────────────────────────────────────
 * Accessibility checker
 * ──────────────────────────────────────────────────── */

export type A11yIssue = {
  id: string;
  severity: "error" | "warn" | "info";
  message: string;
  suggestion?: string;
  presetSuggestion?: string;
};

function pxNum(v: string): number {
  const m = /([\d.]+)/.exec(v);
  return m ? parseFloat(m[1]) : NaN;
}

function emToNumber(v: string): number {
  if (v.endsWith("em")) return parseFloat(v);
  if (v === "0" || v === "normal") return 0;
  if (v.endsWith("px")) return parseFloat(v) / 16;
  return NaN;
}

export function runA11yChecks(): A11yIssue[] {
  const issues: A11yIssue[] = [];
  const labelSize = pxNum(readVar("--banner-label-size"));
  const itemSize = pxNum(readVar("--banner-item-size"));
  const labelTracking = emToNumber(readVar("--banner-label-tracking"));
  const itemTracking = emToNumber(readVar("--banner-item-tracking"));
  const itemLeading = parseFloat(readVar("--banner-item-leading") || "1.35");
  const labelCase = (readVar("--banner-label-case") || "uppercase").toLowerCase();

  if (Number.isFinite(labelSize) && labelSize < 10) {
    issues.push({
      id: "label-size-min",
      severity: "error",
      message: `Banner label is ${labelSize}px — below the 10px readability floor.`,
      suggestion: "Increase to at least 11px.",
      presetSuggestion: "default",
    });
  }
  if (Number.isFinite(itemSize) && itemSize < 12) {
    issues.push({
      id: "item-size-min",
      severity: "error",
      message: `Menu item text is ${itemSize}px — too small for sustained reading.`,
      suggestion: "Use 13–14px for menu items.",
      presetSuggestion: "default",
    });
  }
  if (labelCase === "uppercase" && Number.isFinite(labelTracking) && labelTracking < 0.05) {
    issues.push({
      id: "uppercase-tracking",
      severity: "warn",
      message: "Uppercase labels need ≥ 0.05em tracking for legibility.",
      suggestion: "Increase --banner-label-tracking to 0.1em or higher.",
    });
  }
  if (Number.isFinite(itemTracking) && itemTracking < -0.02) {
    issues.push({
      id: "item-tracking-tight",
      severity: "warn",
      message: "Menu items are tracked too tight — characters may collide.",
      suggestion: "Set --banner-item-tracking to 0 or -0.005em.",
    });
  }
  if (Number.isFinite(itemLeading) && itemLeading < 1.2) {
    issues.push({
      id: "item-leading",
      severity: "warn",
      message: `Item line-height ${itemLeading} is below 1.2 — descenders may clip.`,
      suggestion: "Use 1.3 or higher for menu rows.",
    });
  }

  // Mobile checks (only if explicit override present)
  const mLabel = pxNum(readVar("--banner-label-size-mobile"));
  const mItem = pxNum(readVar("--banner-item-size-mobile"));
  if (Number.isFinite(mLabel) && mLabel < 11) {
    issues.push({
      id: "mobile-label-size",
      severity: "warn",
      message: `Mobile label size ${mLabel}px is small for touch UIs.`,
      suggestion: "Use ≥ 11px on mobile.",
    });
  }
  if (Number.isFinite(mItem) && mItem < 13) {
    issues.push({
      id: "mobile-item-size",
      severity: "warn",
      message: `Mobile menu item size ${mItem}px may be hard to tap-read.`,
      suggestion: "Use ≥ 14px on mobile.",
    });
  }

  if (issues.length === 0) {
    issues.push({
      id: "ok",
      severity: "info",
      message: "No accessibility issues detected.",
    });
  }
  return issues;
}

/* ────────────────────────────────────────────────────
 * Glass-card snapshot (for diff preview)
 * ──────────────────────────────────────────────────── */

export type TypoSnapshot = {
  fontFamily: string;
  fontSize: string;
  letterSpacing: string;
  fontWeight: string;
  textTransform: string;
  lineHeight: string;
};

export function snapshotElement(selector: string): TypoSnapshot | null {
  if (typeof document === "undefined") return null;
  const el = document.querySelector(selector) as HTMLElement | null;
  if (!el) return null;
  const cs = getComputedStyle(el);
  return {
    fontFamily: cs.fontFamily,
    fontSize: cs.fontSize,
    letterSpacing: cs.letterSpacing,
    fontWeight: cs.fontWeight,
    textTransform: cs.textTransform,
    lineHeight: cs.lineHeight,
  };
}

export function snapshotBannerLabel(): TypoSnapshot | null {
  return snapshotElement('[data-sidebar="group-label"]');
}

export function snapshotGlassCardLabel(): TypoSnapshot | null {
  return snapshotElement("[data-glass-card-label]");
}

/* ────────────────────────────────────────────────────
 * Export
 * ──────────────────────────────────────────────────── */

export type ExportPayload = {
  version: 1;
  exportedAt: string;
  state: StoredState;
  resolvedTokens: Record<string, string>;
  glassCardSnapshot: TypoSnapshot | null;
  bannerLabelSnapshot: TypoSnapshot | null;
};

export function buildExport(state: StoredState): ExportPayload {
  const tokens: Record<string, string> = {};
  [...BANNER_TOKENS, ...MOBILE_BANNER_TOKENS].forEach((t) => {
    const v = readVar(t);
    if (v) tokens[t] = v;
  });
  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    state,
    resolvedTokens: tokens,
    glassCardSnapshot: snapshotGlassCardLabel(),
    bannerLabelSnapshot: snapshotBannerLabel(),
  };
}

export function downloadExport(state: StoredState) {
  const payload = buildExport(state);
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `vazoul-typography-${payload.state.preset}-${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ────────────────────────────────────────────────────
 * Profile sync (cross-device)
 * ──────────────────────────────────────────────────── */

export async function loadFromProfile(): Promise<StoredState | null> {
  try {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user?.id) return null;
    const { data, error } = await (supabase as any)
      .from("profiles")
      .select("banner_typography")
      .eq("user_id", auth.user.id)
      .maybeSingle();
    if (error || !data) return null;
    const raw = data.banner_typography;
    if (!raw) return null;
    return raw as StoredState;
  } catch {
    return null;
  }
}

export async function saveToProfile(state: StoredState): Promise<{ ok: boolean; error?: string }> {
  try {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user?.id) return { ok: false, error: "Not signed in" };
    const { error } = await (supabase as any)
      .from("profiles")
      .update({ banner_typography: state })
      .eq("user_id", auth.user.id);
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: e?.message ?? "Unknown error" };
  }
}

/* ────────────────────────────────────────────────────
 * Wizard recommendations
 * ──────────────────────────────────────────────────── */

export type WizardFlavor = "balanced" | "dense" | "editorial" | "calm";

export const FLAVOR_TO_PRESET: Record<WizardFlavor, string> = {
  balanced: "default",
  dense: "compact",
  editorial: "editorial",
  calm: "minimal",
};

export const FLAVOR_META: Record<WizardFlavor, { label: string; description: string }> = {
  balanced: { label: "Balanced", description: "Premium look with high legibility. Recommended for most users." },
  dense: { label: "Dense / Operator", description: "Compact terminal feel — fits more rows on screen." },
  editorial: { label: "Editorial", description: "Magazine-style display headers. Higher visual impact." },
  calm: { label: "Calm / Minimal", description: "All-Inter, low contrast. Quiet and uniform." },
};

/* ────────────────────────────────────────────────────
 * Share links — encode preset+overrides into URL
 * ──────────────────────────────────────────────────── */

const SHARE_PARAM = "typo";

function b64urlEncode(s: string): string {
  if (typeof window === "undefined") return "";
  return btoa(unescape(encodeURIComponent(s)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function b64urlDecode(s: string): string {
  if (typeof window === "undefined") return "";
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const norm = s.replace(/-/g, "+").replace(/_/g, "/") + pad;
  try { return decodeURIComponent(escape(atob(norm))); } catch { return ""; }
}

export function buildShareLink(state: StoredState): string {
  if (typeof window === "undefined") return "";
  const url = new URL(window.location.href);
  url.search = ""; // clean
  url.pathname = "/dev/typography";
  url.searchParams.set(SHARE_PARAM, b64urlEncode(JSON.stringify(state)));
  return url.toString();
}

export function parseShareLinkFromUrl(): StoredState | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get(SHARE_PARAM);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(b64urlDecode(raw));
    if (parsed && typeof parsed === "object" && typeof parsed.preset === "string") {
      return parsed as StoredState;
    }
  } catch { /* noop */ }
  return null;
}

/* ────────────────────────────────────────────────────
 * Change history — undo + restore last saved
 * ──────────────────────────────────────────────────── */

const HISTORY_KEY = "vzl_banner_typo_history_v1";
const LAST_SAVED_KEY = "vzl_banner_typo_lastsaved_v1";
const HISTORY_LIMIT = 25;

export type HistoryEntry = {
  at: string;
  state: StoredState;
  label?: string;
};

export function loadHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? (JSON.parse(raw) as HistoryEntry[]) : [];
  } catch { return []; }
}

export function pushHistory(state: StoredState, label?: string) {
  try {
    const list = loadHistory();
    list.push({ at: new Date().toISOString(), state, label });
    while (list.length > HISTORY_LIMIT) list.shift();
    localStorage.setItem(HISTORY_KEY, JSON.stringify(list));
  } catch {/* noop */}
}

export function popHistory(): HistoryEntry | null {
  try {
    const list = loadHistory();
    if (list.length === 0) return null;
    const removed = list.pop()!;
    localStorage.setItem(HISTORY_KEY, JSON.stringify(list));
    return removed;
  } catch { return null; }
}

export function peekPreviousHistory(): HistoryEntry | null {
  const list = loadHistory();
  return list.length >= 2 ? list[list.length - 2] : null;
}

export function clearHistory() {
  try { localStorage.removeItem(HISTORY_KEY); } catch {/* noop */}
}

export function markLastSaved(state: StoredState) {
  try { localStorage.setItem(LAST_SAVED_KEY, JSON.stringify(state)); } catch {/* noop */}
}

export function loadLastSaved(): StoredState | null {
  try {
    const raw = localStorage.getItem(LAST_SAVED_KEY);
    return raw ? (JSON.parse(raw) as StoredState) : null;
  } catch { return null; }
}

/* ────────────────────────────────────────────────────
 * Token-level validator (per-CSS-variable)
 * ──────────────────────────────────────────────────── */

export type TokenIssue = {
  token: string;
  currentValue: string;
  suggestedValue: string;
  severity: "error" | "warn";
  reason: string;
};

export function validateTokens(): TokenIssue[] {
  const issues: TokenIssue[] = [];
  const labelSize = pxNum(readVar("--banner-label-size"));
  const itemSize = pxNum(readVar("--banner-item-size"));
  const labelTracking = emToNumber(readVar("--banner-label-tracking"));
  const itemTracking = emToNumber(readVar("--banner-item-tracking"));
  const itemLeading = parseFloat(readVar("--banner-item-leading") || "1.35");
  const labelCase = (readVar("--banner-label-case") || "uppercase").toLowerCase();
  const mLabel = pxNum(readVar("--banner-label-size-mobile"));
  const mItem = pxNum(readVar("--banner-item-size-mobile"));

  if (Number.isFinite(labelSize) && labelSize < 10) {
    issues.push({
      token: "--banner-label-size",
      currentValue: `${labelSize}px`,
      suggestedValue: "11px",
      severity: "error",
      reason: "Below 10px readability floor.",
    });
  }
  if (Number.isFinite(itemSize) && itemSize < 12) {
    issues.push({
      token: "--banner-item-size",
      currentValue: `${itemSize}px`,
      suggestedValue: "13.5px",
      severity: "error",
      reason: "Too small for sustained reading.",
    });
  }
  if (labelCase === "uppercase" && Number.isFinite(labelTracking) && labelTracking < 0.05) {
    issues.push({
      token: "--banner-label-tracking",
      currentValue: `${labelTracking}em`,
      suggestedValue: "0.12em",
      severity: "warn",
      reason: "Uppercase needs ≥ 0.05em tracking.",
    });
  }
  if (Number.isFinite(itemTracking) && itemTracking < -0.02) {
    issues.push({
      token: "--banner-item-tracking",
      currentValue: `${itemTracking}em`,
      suggestedValue: "-0.005em",
      severity: "warn",
      reason: "Tracked too tight — characters may collide.",
    });
  }
  if (Number.isFinite(itemLeading) && itemLeading < 1.2) {
    issues.push({
      token: "--banner-item-leading",
      currentValue: String(itemLeading),
      suggestedValue: "1.35",
      severity: "warn",
      reason: "Line-height below 1.2 — descenders may clip.",
    });
  }
  if (Number.isFinite(mLabel) && mLabel < 11) {
    issues.push({
      token: "--banner-label-size-mobile",
      currentValue: `${mLabel}px`,
      suggestedValue: "11px",
      severity: "warn",
      reason: "Mobile labels should be ≥ 11px.",
    });
  }
  if (Number.isFinite(mItem) && mItem < 13) {
    issues.push({
      token: "--banner-item-size-mobile",
      currentValue: `${mItem}px`,
      suggestedValue: "14px",
      severity: "warn",
      reason: "Mobile items should be ≥ 13px.",
    });
  }
  return issues;
}

/** Apply a single token correction to state.customVars / mobileVars. */
export function applyTokenFix(state: StoredState, issue: TokenIssue): StoredState {
  const isMobile = issue.token.endsWith("-mobile");
  if (isMobile) {
    return {
      ...state,
      mobileVars: { ...(state.mobileVars ?? {}), [issue.token]: issue.suggestedValue } as any,
    };
  }
  return {
    ...state,
    customVars: { ...(state.customVars ?? {}), [issue.token]: issue.suggestedValue } as any,
  };
}

export function applyAllTokenFixes(state: StoredState, issues: TokenIssue[]): StoredState {
  return issues.reduce((s, iss) => applyTokenFix(s, iss), state);
}

/* ────────────────────────────────────────────────────
 * Font fallback mode
 * ──────────────────────────────────────────────────── */

export type FallbackMode = "auto" | "system" | "serif" | "keep";

export const FALLBACK_KEY = "vzl_typo_fallback_v1";

export const FALLBACK_META: Record<FallbackMode, { label: string; description: string }> = {
  auto: { label: "Auto (default)", description: "Use bundled webfonts; rely on built-in CSS fallbacks if they fail." },
  system: { label: "System UI", description: "If a webfont fails, switch to the OS UI font stack." },
  serif: { label: "Serif fallback", description: "If a webfont fails, switch to a high-readability serif stack." },
  keep: { label: "Keep webfonts", description: "Block fallback swap; show FOUT until webfonts arrive." },
};

const FALLBACK_STACKS: Record<Exclude<FallbackMode, "auto" | "keep">, { display: string; sans: string; mono: string }> = {
  system: {
    display: `system-ui, -apple-system, "Segoe UI", sans-serif`,
    sans: `system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`,
    mono: `ui-monospace, "SF Mono", Menlo, monospace`,
  },
  serif: {
    display: `Georgia, "Times New Roman", serif`,
    sans: `Georgia, "Times New Roman", serif`,
    mono: `"Courier New", monospace`,
  },
};

export function loadFallbackMode(): FallbackMode {
  try {
    const raw = localStorage.getItem(FALLBACK_KEY);
    if (raw && ["auto", "system", "serif", "keep"].includes(raw)) return raw as FallbackMode;
  } catch {/* noop */}
  return "auto";
}

export function saveFallbackMode(mode: FallbackMode) {
  try { localStorage.setItem(FALLBACK_KEY, mode); } catch {/* noop */}
}

/**
 * Apply fallback mode by checking which fonts loaded and overriding
 * --font-display / --font-sans / --font-mono if the webfont failed.
 */
export async function applyFallbackMode(mode: FallbackMode) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  // reset our overrides first
  root.style.removeProperty("--font-display");
  root.style.removeProperty("--font-sans");
  root.style.removeProperty("--font-mono");

  if (mode === "auto") return;

  if (mode === "keep") {
    // force webfont families with no fallback chain
    root.style.setProperty("--font-display", `"Space Grotesk"`);
    root.style.setProperty("--font-sans", `"Inter"`);
    root.style.setProperty("--font-mono", `"JetBrains Mono"`);
    return;
  }

  const stack = FALLBACK_STACKS[mode];
  const fonts = await checkFontsLoaded();
  const map: Record<FontRole, "display" | "sans" | "mono"> = {
    display: "display", body: "sans", mono: "mono",
  };
  const cssVar: Record<"display" | "sans" | "mono", string> = {
    display: "--font-display", sans: "--font-sans", mono: "--font-mono",
  };
  const webfont: Record<"display" | "sans" | "mono", string> = {
    display: `"Space Grotesk"`, sans: `"Inter"`, mono: `"JetBrains Mono"`,
  };
  fonts.forEach((f) => {
    const slot = map[f.role];
    const value = f.loaded ? `${webfont[slot]}, ${stack[slot]}` : stack[slot];
    root.style.setProperty(cssVar[slot], value);
  });
}
